import urijs from 'urijs';
/**
 * @ngdoc service
 * @name appUtils
 * @description
 * This service is meant to have generic utility methods that the app may need and that can't be
 * categorized in any of the existing modules.
 *
 * @memberof common
 */
class AppUtils {
  /**
   * Class constructor.
   * @param {Object} appConstant The app configuration dictionary.
   * @ignore
   */
  constructor(appConstant) {
    'ngInject';

    /**
     * The local reference to the `permissions` object.
     * @type {Object}
     */
    this.permissions = appConstant.permissions;
  }
  /**
   * Replace plaholders on a URL. If one of more of the placeholders are not present on the URL,
   * the method will dynamically add them as query string arguments.
   *
   * The reason we use this instead of `$interpolate` it's because $interpolate` does a LOT more
   * than just a replacement, it's not for URLs specific and the query string _feature_ would
   * look like an ugly path in top of that.
   *
   * @example
   * console.log(formatURL('http://{server}/login', { server: 'olapic.com' }));
   * // http://olapic.com/login
   *
   * console.log(formatURL('http://{server}/login', { server: 'olapic.com', version: '2.0' }));
   * // http://olapic.com/login?version=2.0
   *
   * @param {String} url        The base URL where the function will replace the placeholders.
   * @param {Object} parameters A dictionary with the placeholders to replace.
   * @return {String}
   */
  formatURL(url, parameters) {
    const optionals = [];
    Object.keys(parameters).forEach((key) => {
      const placeholder = `{${key}}`;
      if (url.includes(placeholder)) {
        url = url.replace(placeholder, parameters[key]);
      } else {
        optionals.push(key);
      }
    });

    if (optionals.length) {
      const uriObj = urijs(url);
      optionals.forEach((key) => {
        uriObj.addQuery(key, parameters[key]);
      });

      url = uriObj.toString();
    }

    return url;
  }
  /**
   * Angular doesn't have an `ng-pluralize` function that can be accessd from a controller, just
   * the directive, so this method imitates its functionality with only one small change: Instead
   * of using words for the numbers, you need to use real numbers.
   * @example
   * pluralize(someValue, {
   *     0: 'No records',
   *     1: 'One record',
   *     other: '{} records',
   * });
   *
   * @param {Number} value   The number to evaluate.
   * @param {Object} options A dictionary with the options. It supports the `other` key.
   */
  pluralize(value, options) {
    let text = '';
    if (options[value]) {
      text = options[value];
    } else if (!options[value] && options.other) {
      text = options.other;
    }

    return text.includes('{}') ? text.replace(/\{\}/g, value) : text;
  }
  /**
   * This method is used for sorting an array of objects by a property of those objects. The
   * returned value of this method should be used insde a `.sort` call.
   * @example
   * [
   *     { name: 'Nightwing' },
   *     { name: 'Red Hood'},
   *     { name: 'Batman'}
   * ].sort(sortObjectsList('name'));
   * // The order would now be 'Batman', 'Nightwing' and 'Red Hood'
   *
   * @param {String} property The name of the objects property you want to sort the array with.
   * @param {String} order    String representing the sort order, 'asc' or 'desc'
   * @return {Function}
   */
  sortObjectsList(property, order = 'asc') {
    const orderN = order === 'asc' ? 1 : -1;
    return (a, b) => {
      const propA = a[property];
      const propB = b[property];
      let result = 0;
      if (propA > propB) {
        result = 1 * orderN;
      } else if (propB > propA) {
        result = -1 * orderN;
      }

      return result;
    };
  }
  /**
   * Validates if a URL has a protocol and in case it's missing, it prepends a default one.
   * @param {String} url      The URL to validate and fix.
   * @param {String} protocol Optional. The default protocol to add in case the URL doesn't have
   *                          one.
   */
  addProtocolToURL(url, protocol = 'http') {
    let result = url || '';
    if (!/^(f|ht)tps?:\/\//i.test(result)) {
      result = `${protocol}://${result}`;
    }

    return result;
  }
  /**
   * Get the pagination data from a response object.
   * @param {Object} response The response to get the pagintaion from.
   */
  getPaginationFromResponse(response) {
    return response.data.metadata && response.data.metadata.pagination ?
      response.data.metadata.pagination :
      { links: {} };
  }
  /**
   * Get a query param from a URL.
   * @param {String} url   The URL to process.
   * @param {String} param The param to get.
   */
  getQueryParam(url, param) {
    return url.split(/[&||?]/).reduce((v, q) => {
      const params = (q || '').split('=');
      return params[0] === param ? params[1] : v;
    });
  }
  /**
   * Get all the admin accounts from a list of accounts.
   * @param {Array} accounts  The list of accounts to search.
   */
  getAdminAccounts(accounts) {
    // Filter the accounts that have all the permissions the permissions.access array has
    return accounts.filter((account) => {
      const hasPermissions = !!account.permissions.length;
      const result = hasPermissions && this.permissions.access
      .every((access) => account.permissions.some((permission) => access.name === permission.name));
      return result;
    });
  }
}

export default AppUtils;



// WEBPACK FOOTER //
// ./src/frontend/modules/common/services/appUtils.js